import React, { useState, useEffect, ReactElement, useRef } from 'react'
import tw, { styled } from 'twin.macro'
import { GatsbyImage, getImage, GatsbyImageProps } from 'gatsby-plugin-image'
import { Link } from 'gatsby'

import { Style as PillStyle } from '@ui-kit/atoms/button'
import MobileIcon from '@assets/icons/mobile.inline.svg';

import { CheckCircle, X, Info } from 'react-feather'

interface MobileMultisaverCalculator {
    heading?: string,
    headingSmall?: string,
    subheading?: string,
    smallprint?: string,
    divide: number,
    footer?: string,
    price: number,
    specialPrice?: number,
    specialText?: string,
    image: ReactElement | null,
    includePromotionText: boolean,
    cta?: string,
    bullets?: Array<string>
}

interface DataPlan {
    data: number,
    cost: number
}

let formatCurrency = new Intl.NumberFormat("en-NZ", {
	style: 'currency',
	currency: 'NZD',
    compactDisplay: 'short',
    minimumFractionDigits: 2,
});

const MobileMultisaverCalculator = ({ heading, headingSmall, subheading, smallprint, divide = 1, price, specialPrice, specialText, footer, image, includePromotionText, bullets, cta, ...props }: MobileMultisaverCalculator) => {
    const [ sims, setSims ] = useState<number>(1);
    const [ data, setData ] = useState<DataPlan>({
        data: 5,
        cost: 0
    })

    price = specialPrice || price;

    return (
        <Style id="calculator" className='promotion--mobile-multisaver-calculator'  {...props}>
            <div className="promotion__container">
                {heading && <h2>{heading}{headingSmall ? <small>{headingSmall}</small> : null}</h2>}
                <form>
                    <div className="fields">
                        <div className="field">
                            <p>How many SIMs would you like?</p>
                            <div className="options">
                            {Array.from({ length: 6 }, (i,o) => (
                                <React.Fragment key={o}>
                                    <input type="radio" name={'sims'} id={`sims-${o}`} defaultChecked={sims === (o+1)} onChange={() => setSims(o+1)} />
                                    <label htmlFor={`sims-${o}`}>{o+1}</label>
                                </React.Fragment>
                            ))}
                            </div>
                        </div>

                        <div className="field">
                            <p>Choose your monthly Max Speed Data:</p>
                            <div className="options">
                                <input type="radio" name={'data'} id={'data-4gb'} defaultChecked={data.cost === 0} onChange={({ target }) => setData({
                                    data: 5,
                                    cost: 0
                                })} />
                                <label htmlFor={'data-4gb'}>
                                    <strong>5GB</strong>
                                </label>
                                <input type="radio" name={'data'} id={'data-8gb'} defaultChecked={data.cost === 10} onChange={({ target }) => setData({
                                    data: 10,
                                    cost: 10 / divide
                                })} />
                                <label htmlFor={'data-8gb'}>
                                    <strong>10GB</strong>
                                    {/*<small>+{formatCurrency.format(10/divide)}</small>*/}
                                </label>
                                <input type="radio" name={'data'} id={'data-16gb'} defaultChecked={data.cost === 20} onChange={({ target }) => setData({
                                    data: 20,
                                    cost: 20 / divide
                                })} />
                                <label htmlFor={'data-16gb'}>
                                    <strong>20GB</strong>
                                    {/*<small>+{formatCurrency.format(20/divide)}</small>*/}
                                </label>
                                <input type="radio" name={'data'} id={'data-32gb'} defaultChecked={data.cost === 30} onChange={({ target }) => setData({
                                    data: 40,
                                    cost: 30 / divide
                                })} />
                                <label htmlFor={'data-32gb'}>
                                    <strong>40GB</strong>
                                    {/*<small>+{formatCurrency.format(30/divide)}</small>*/}
                                </label>
                            </div>
                        </div>

                    </div>

                    <output>
                        {subheading && <h2><span>{subheading}</span> <MobileIcon /></h2>}
                        <div className="detail">
                            <h3>{formatCurrency.format((sims * (price / divide)) + data.cost)} <span>/month</span> <small className={specialText ? 'block' : ''}>for {sims} SIM{sims > 1 ? 's' : ''} {specialText}</small></h3>
                            <p><strong>Average cost per SIM:</strong> <mark>{formatCurrency.format(((sims * (price / divide)) + data.cost) / sims)}</mark> / month</p>
                            <p><strong>Each SIM gets:</strong> endless Data, including <mark>{data.data}GB</mark> at max speed each month. Plus endless calls and texts to NZ and Australia.</p>
                            {smallprint && <p><small>{smallprint}</small></p>}
                        </div>

                        {includePromotionText && (
                        <div className="promo-area">
                            <p>Sign up and get <mark>3 months free*</mark></p>
                            {image}
                        </div>)}

                        <div tw="text-center mt-8">
                            {cta ? <Link to={cta}>Sign up now</Link> : <a href={'/join'}>Sign up now</a>}
                        </div>

                    </output>


                </form>
            </div>
        </Style>
    )
}
export default MobileMultisaverCalculator


const Style = styled.section`
    --pink-light: #d085a2;

    .promotion__container {
        ${tw `mx-auto max-w-5xl px-4 sm:px-4 my-10 lg:my-20`}
    }

    h2 {
        ${tw `font-bold text-center mb-8 lg:mb-12 antialiased text-3xl lg:text-4xl`}
        text-wrap: balance;

        small {
            ${tw `block text-2xl font-semibold`}
        }
    }

    form {
        ${tw `grid grid-cols-12 md:gap-x-7 lg:gap-x-8 xl:gap-x-16 gap-y-6`}


        > .fields {
            ${tw `col-span-12 lg:col-span-6 space-y-4`}

            .options {
                ${tw `flex gap-x-2 relative`}
            }

            input {
                ${tw `opacity-0 absolute z-0`}
            }

            input:checked + label {
                ${tw `bg-primary text-white cursor-default`}
            }

            .field > p {
                ${tw `font-semibold text-lg mb-2`}
            }

            label {
                ${tw `border border-primary p-4 rounded flex flex-col items-center text-xl flex-1 cursor-pointer font-semibold leading-tight flex-1`}
            }
        }

        output {
            ${tw `col-span-12 sm:col-start-2 sm:col-span-10 md:col-span-8 md:col-start-3 lg:col-start-7 lg:col-span-6 flex flex-col rounded-xl px-6 pt-6 pb-10`}
            box-shadow: 0 0 1.5em rgba(0, 0, 0, .35);

            h2 {
                ${tw `text-[var(--nova-pink)] text-left text-3xl lg:text-4xl mb-5 flex gap-x-4 items-center justify-between`}

                span {
                    ${tw `leading-snug`}
                }

                svg {
                    ${tw `rotate[18deg] lg:scale[1.2]`}
                }
            }

            .detail {
                ${tw `rounded-xl border[3px solid var(--pink-light)] p-5`}

                h3 {
                    ${tw `font-semibold whitespace-nowrap text-3xl md:text-4xl xl:text-6xl mb-4 tracking-tight`}

                    small {
                        ${tw `text-[var(--pink-light)] block w-full text-base tracking-normal whitespace-normal leading-none! mt-2`}
                    }

                    span {
                        ${tw `text-xl tracking-normal md:align-middle md:inline-block md:mt-[.75em]`}
                    }
                }

                p {
                    ${tw `tracking-tight`}

                    + p {
                        ${tw `mt-1`}
                    }
                }

                mark {
                    ${tw `bg-transparent text-[var(--pink-light)] text-xl lg:text-2xl font-semibold`}
                }
            }

            a {
                border: 1px transparent solid;
                border-radius: 48px;
                padding: 0 1.5em;
                min-height: 48px;
                line-height: 48px;
                outline: none;
                ${tw `bg-primary mx-auto lg:min-w-[18ch] text-center justify-center text-black! text-lg font-semibold truncate inline-flex items-center hover:bg-black hover:text-white! focus-visible:bg-black focus-visible:text-white!`}
            }
        }

        .smallprint {
            ${tw `text-sm text-gray-500 lg:mt-4! block`}

            a {
                ${tw `underline `}
            }
        }
        > .smallprint {
            ${tw `col-span-12 block`}
        }
    }

    footer {
        ${tw `text-sm mt-6 lg:mt-8 text-gray-500 text-center`}

        strong {
            ${tw `font-semibold`}
        }
    }


    .gatsby-image-wrapper {
        ${tw `text-center hidden lg:display[inherit] aspect-square overflow-hidden`}
        grid-column: 2 / 4;
        grid-row: 1 / 2;
    }

    .promo-area {
        ${tw `text-center`}

        img {
            ${tw ` object-cover`}
        }
    }
    .promo-area p {
        ${tw `font-bold! antialiased text-pink text-2xl! leading-snug`}
        grid-column: 1 / 3;
        grid-row: 1 / 2;

        mark {
            ${tw `mt-2 inline-block rounded-xl shadow-lg text-3xl lg:text-4xl bg-pink text-white p-4`}
        }

        span {
            ${tw `inline-block shadow-lg text-3xl lg:text-4xl bg-pink text-white p-4`}

            &:first-of-type {
                ${tw `rounded-t-xl rounded-br-xl`}
            }

            &:last-of-type {
                ${tw `rounded-b-xl`}
            }

            + span {
                ${tw `pt-0`}
            }
        }

    }
`